import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import {
  getRepositoryVariables,
  createRepositoryVariable,
  updateRepositoryVariable,
  deleteRepositoryVariable,
  bulkCreateVariables,
  bulkDeleteVariables,
  getUserRepositories,
  copyVariablesToRepositories,
} from "../api";
import { useAuth } from "../contexts/AuthContext";
import { useToast } from "../hooks/use-toast";
import { Button } from "../components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import {
  Plus,
  Trash2,
  Upload,
  Download,
  Copy,
  AlertCircle,
} from "lucide-react";
import { Alert, AlertDescription, AlertTitle } from "../components/ui/alert";
import { analyzeMissingVariables } from "../utils/variableAnalyzer";

// Import our new components
import VariableTable from "../components/variables/VariableTable";
import VariableDialog from "../components/variables/VariableDialog";
import ImportDialog from "../components/variables/ImportDialog";
import CopyDialog from "../components/variables/CopyDialog";

const VariablesPage = () => {
  const { workspace, repoSlug } = useParams();
  const { logout } = useAuth();
  const { toast } = useToast();

  const [variables, setVariables] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedVariables, setSelectedVariables] = useState([]);
  const [missingSuggestions, setMissingSuggestions] = useState([]);

  // Dialog states
  const [openDialog, setOpenDialog] = useState(false);
  const [dialogMode, setDialogMode] = useState("add"); // 'add', 'edit', 'import', 'copy'
  const [currentVariable, setCurrentVariable] = useState({
    key: "",
    value: "",
    secured: false,
  });

  // New state variables
  const [targetRepositories, setTargetRepositories] = useState([]);
  const [selectedTargetRepos, setSelectedTargetRepos] = useState([]);
  const [loadingRepositories, setLoadingRepositories] = useState(false);
  const [importText, setImportText] = useState("");
  const [repoSearchTerm, setRepoSearchTerm] = useState("");
  const [allRepositories, setAllRepositories] = useState([]);

  // New state for tracking copy operation
  const [copyInProgress, setCopyInProgress] = useState(false);

  useEffect(() => {
    fetchVariables();
    // Fetch when the route points at a different repository.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspace, repoSlug]);

  useEffect(() => {
    if (variables.length > 0) {
      // Use the utility function to analyze variables
      const suggestions = analyzeMissingVariables(variables);
      setMissingSuggestions(suggestions);
    }
  }, [variables]);

  // New effect to filter repositories when search term or all repositories change
  useEffect(() => {
    // Get the filtered projects from localStorage
    const filteredProjects = JSON.parse(
      localStorage.getItem("filteredProjects") || "[]"
    );

    // Filter repositories based on search term and filtered projects
    if (Array.isArray(allRepositories)) {
      const filtered = allRepositories.filter((repo) => {
        // First apply the search term filter
        const matchesSearch =
          !repoSearchTerm ||
          repo.name.toLowerCase().includes(repoSearchTerm.toLowerCase()) ||
          repo.slug.toLowerCase().includes(repoSearchTerm.toLowerCase());

        // Then apply project filter if there are filtered projects
        // Check both project key and project UUID to be thorough
        let projectMatch = true;
        if (filteredProjects.length > 0 && repo.project) {
          projectMatch = !filteredProjects.includes(repo.project.key);
        }

        // Only include repositories that start with "frontend"
        const isFrontendRepo =
          repo.name.toLowerCase().startsWith("frontend") ||
          repo.slug.toLowerCase().startsWith("frontend");

        return matchesSearch && projectMatch && isFrontendRepo;
      });

      setTargetRepositories(filtered);

      // Debug
      console.log("Applied repository filtering:", {
        before: allRepositories.length,
        after: filtered.length,
        filteredProjects,
        searchTerm: repoSearchTerm,
        frontendOnly: true,
      });
    }
  }, [repoSearchTerm, allRepositories]);

  const fetchVariables = async () => {
    try {
      setLoading(true);
      const data = await getRepositoryVariables(workspace, repoSlug);
      setVariables(data);
      setLoading(false);
    } catch (err) {
      setError("Failed to load variables");
      setLoading(false);
      if (err.response && err.response.status === 401) {
        logout();
      }
    }
  };

  const handleAddSuggestion = (key) => {
    setCurrentVariable({ key, value: "", secured: false });
    setDialogMode("add");
    setOpenDialog(true);
  };

  const handleOpenAddDialog = () => {
    setCurrentVariable({ key: "", value: "", secured: false });
    setDialogMode("add");
    setOpenDialog(true);
  };

  const handleOpenEditDialog = (variable) => {
    // Check if only value was changed (inline edit)
    if ("_inlineEdit" in variable) {
      handleQuickValueUpdate(variable);
      return;
    }

    setCurrentVariable({
      uuid: variable.uuid,
      key: variable.key,
      value: variable.value || "",
      secured: variable.secured || false,
    });
    setDialogMode("edit");
    setOpenDialog(true);
  };

  const handleQuickValueUpdate = async (variable) => {
    try {
      await updateRepositoryVariable(workspace, repoSlug, variable.uuid, {
        value: variable.value,
      });

      // Update local state instead of refetching all variables
      setVariables((prev) =>
        prev.map((v) =>
          v.uuid === variable.uuid ? { ...v, value: variable.value } : v
        )
      );

      toast({
        title: "Success",
        description: "Variable value updated",
      });
    } catch (err) {
      toast({
        title: "Error",
        description: "Failed to update variable value",
        variant: "destructive",
      });
    }
  };

  const handleOpenImportDialog = () => {
    setImportText("");
    setDialogMode("import");
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setCurrentVariable((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleCheckboxChange = (checked) => {
    setCurrentVariable((prev) => ({
      ...prev,
      secured: checked,
    }));
  };

  const handleSaveVariable = async () => {
    try {
      if (dialogMode === "add") {
        const newVariable = await createRepositoryVariable(
          workspace,
          repoSlug,
          currentVariable
        );
        toast({
          title: "Success",
          description: "Variable created successfully",
        });
        // Add the new variable to local state
        setVariables((prev) => [...prev, newVariable]);
      } else if (dialogMode === "edit") {
        await updateRepositoryVariable(
          workspace,
          repoSlug,
          currentVariable.uuid,
          currentVariable
        );
        // Update local state
        setVariables((prev) =>
          prev.map((v) =>
            v.uuid === currentVariable.uuid ? { ...v, ...currentVariable } : v
          )
        );
        toast({
          title: "Success",
          description: "Variable updated successfully",
        });
      }

      handleCloseDialog();
    } catch (err) {
      toast({
        title: "Error",
        description: "Failed to save variable",
        variant: "destructive",
      });
    }
  };

  const handleDeleteVariable = async (uuid) => {
    try {
      await deleteRepositoryVariable(workspace, repoSlug, uuid);
      // Update local state
      setVariables((prev) => prev.filter((v) => v.uuid !== uuid));
      toast({
        title: "Success",
        description: "Variable deleted successfully",
      });
    } catch (err) {
      toast({
        title: "Error",
        description: "Failed to delete variable",
        variant: "destructive",
      });
    }
  };

  const handleExportVariables = () => {
    const dataToExport = variables.map(({ key, value, secured }) => ({
      key,
      value,
      secured,
    }));

    const jsonString = JSON.stringify(dataToExport, null, 2);
    const blob = new Blob([jsonString], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${repoSlug}-variables.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);

    toast({
      title: "Success",
      description: "Variables exported successfully",
    });
  };

  const handleImportVariables = async () => {
    try {
      let importData;
      try {
        importData = JSON.parse(importText);
      } catch (err) {
        toast({
          title: "Error",
          description: "Invalid JSON format",
          variant: "destructive",
        });
        return;
      }

      if (!Array.isArray(importData)) {
        toast({
          title: "Error",
          description: "Import data must be an array",
          variant: "destructive",
        });
        return;
      }

      // Create new variables
      const validVariables = importData.filter(
        (v) => v.key && (v.value !== undefined || v.secured)
      );
      const newVariables = await bulkCreateVariables(
        workspace,
        repoSlug,
        validVariables
      );

      // Update local state with new variables
      setVariables((prev) => [...prev, ...newVariables]);

      toast({
        title: "Success",
        description: `${validVariables.length} variables imported successfully`,
      });

      handleCloseDialog();
    } catch (err) {
      toast({
        title: "Error",
        description: "Failed to import variables",
        variant: "destructive",
      });
    }
  };

  const handleBulkDelete = async () => {
    if (selectedVariables.length === 0) {
      toast({
        title: "Info",
        description: "No variables selected",
      });
      return;
    }

    try {
      await bulkDeleteVariables(workspace, repoSlug, selectedVariables);

      // Update local state
      setVariables((prev) =>
        prev.filter((v) => !selectedVariables.includes(v.uuid))
      );

      toast({
        title: "Success",
        description: `${selectedVariables.length} variables deleted successfully`,
      });
      setSelectedVariables([]);
    } catch (err) {
      toast({
        title: "Error",
        description: "Failed to delete variables",
        variant: "destructive",
      });
    }
  };

  const handleSelectVariable = (uuid) => {
    setSelectedVariables((prev) =>
      prev.includes(uuid) ? prev.filter((id) => id !== uuid) : [...prev, uuid]
    );
  };

  const handleSelectAll = (selectAll, filteredVariableUuids) => {
    if (selectAll) {
      // If filtering is active, only select the filtered variables
      if (filteredVariableUuids) {
        setSelectedVariables((prev) => {
          const newSelection = [...prev];
          filteredVariableUuids.forEach((uuid) => {
            if (!newSelection.includes(uuid)) {
              newSelection.push(uuid);
            }
          });
          return newSelection;
        });
      } else {
        // No filtering, select all variables
        setSelectedVariables(variables.map((v) => v.uuid));
      }
    } else {
      // If filtering is active, only deselect the filtered variables
      if (filteredVariableUuids) {
        setSelectedVariables((prev) =>
          prev.filter((uuid) => !filteredVariableUuids.includes(uuid))
        );
      } else {
        // No filtering, deselect all variables
        setSelectedVariables([]);
      }
    }
  };

  const handleOpenCopyDialog = async () => {
    if (selectedVariables.length === 0) {
      toast({
        title: "Info",
        description: "No variables selected to copy",
      });
      return;
    }

    try {
      setLoadingRepositories(true);
      setRepoSearchTerm("");
      setOpenDialog(true);
      setDialogMode("copy");

      // Clear existing repositories first for better UX
      setAllRepositories([]);
      setTargetRepositories([]);

      // Fetch repositories from the API
      const repos = await getUserRepositories(workspace);
      console.log("Fetched repositories:", repos.length);

      // Filter out the current repository
      const filteredRepos = repos.filter((repo) => repo.slug !== repoSlug);

      // Log some sample data for debugging
      if (filteredRepos.length > 0) {
        console.log("Sample repository data:", {
          first: filteredRepos[0],
          hasProject: !!filteredRepos[0].project,
          projectKey: filteredRepos[0].project?.key,
        });
      }

      setAllRepositories(filteredRepos);
      // The targetRepositories will be set by the useEffect after allRepositories is updated

      setSelectedTargetRepos([]);
      setLoadingRepositories(false);
    } catch (err) {
      console.error("Error loading repositories:", err);
      toast({
        title: "Error",
        description: "Failed to load repositories",
        variant: "destructive",
      });
      setLoadingRepositories(false);
    }
  };

  const handleToggleTargetRepo = (repoSlug) => {
    setSelectedTargetRepos((prev) =>
      prev.includes(repoSlug)
        ? prev.filter((slug) => slug !== repoSlug)
        : [...prev, repoSlug]
    );
  };

  const handleCopyVariables = async () => {
    if (selectedTargetRepos.length === 0) {
      toast({
        title: "Info",
        description: "No target repositories selected",
      });
      return;
    }

    // Get the variables to copy
    const variablesToCopy = variables.filter((v) =>
      selectedVariables.includes(v.uuid)
    );

    // Close the dialog immediately
    handleCloseDialog();

    // Show initial toast
    const initialToastId = toast({
      title: "Copying Variables",
      description: `Starting copy operation`,
      duration: 100000, // Keep it visible until complete
    });

    // Set copy in progress flag
    setCopyInProgress(true);

    // Progress callback function to update UI
    const updateProgress = (progress) => {
      // Update the toast with current progress
      toast({
        id: initialToastId,
        title: "Copying Variables",
        description: progress.status,
        duration: 100000,
      });
    };

    try {
      // Perform the copy operation asynchronously with progress updates
      const response = await copyVariablesToRepositories(
        workspace,
        repoSlug,
        selectedTargetRepos,
        variablesToCopy,
        updateProgress
      );

      // Get the summary from the response
      const { summary } = response;

      // Show final toast with results
      if (summary.totalFailed === 0) {
        toast({
          title: "Success",
          description: `All ${summary.totalCreated} variables were successfully copied to ${summary.totalRepositories} repositories.`,
        });
      } else {
        toast({
          title: "Partial Success",
          description: `${summary.totalCreated} variables copied successfully, ${summary.totalFailed} failed across ${summary.totalRepositories} repositories.`,
          variant: "warning",
        });
      }
    } catch (err) {
      toast({
        title: "Error",
        description:
          "Failed to copy variables: " + (err.message || "Unknown error"),
        variant: "destructive",
      });
    } finally {
      setCopyInProgress(false);
    }
  };

  const handleRepoSearchChange = (e) => {
    // Safely get the search value
    const value = e?.target?.value ?? "";
    setRepoSearchTerm(value);
    // The filtering will happen in the useEffect
  };

  if (error) {
    return (
      <div className="flex justify-center items-center h-[80vh]">
        <div className="text-destructive text-lg font-semibold">{error}</div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex justify-center items-center h-[80vh]">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="container mx-auto py-8 px-4">
      <div className="flex flex-col sm:flex-row justify-between items-center mb-6">
        <div className="flex items-center mb-4 sm:mb-0">
          {/* Breadcrumbs removed as they're now in the main layout */}
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" size="sm" onClick={handleOpenAddDialog}>
            <Plus className="mr-2 h-4 w-4" />
            Add Variable
          </Button>
          <Button variant="outline" size="sm" onClick={handleOpenImportDialog}>
            <Upload className="mr-2 h-4 w-4" />
            Import
          </Button>
          <Button variant="outline" size="sm" onClick={handleExportVariables}>
            <Download className="mr-2 h-4 w-4" />
            Export
          </Button>
          {selectedVariables.length > 0 && (
            <>
              <Button
                variant="outline"
                size="sm"
                onClick={handleOpenCopyDialog}
                disabled={copyInProgress}
              >
                <Copy className="mr-2 h-4 w-4" />
                Copy ({selectedVariables.length})
              </Button>
              <Button
                variant="destructive"
                size="sm"
                onClick={handleBulkDelete}
              >
                <Trash2 className="mr-2 h-4 w-4" />
                Delete ({selectedVariables.length})
              </Button>
            </>
          )}
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Variables for {repoSlug}</CardTitle>
          <CardDescription>
            Manage repository variables that can be used in pipelines and
            deployments
          </CardDescription>
        </CardHeader>
        <CardContent>
          {missingSuggestions.length > 0 && (
            <Alert className="mb-4 bg-amber-50 border-amber-300">
              <div className="flex items-start">
                <AlertCircle className="h-4 w-4 text-amber-600 mt-1 mr-2 flex-shrink-0" />
                <div>
                  <AlertTitle className="text-amber-800">
                    Potential Missing Variables
                  </AlertTitle>
                  <AlertDescription className="text-amber-700">
                    <p className="mb-2">
                      Based on your existing variables, you might want to add:
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {missingSuggestions.map((suggestion, index) => (
                        <Button
                          key={index}
                          variant="outline"
                          size="sm"
                          className="bg-white border-amber-400 text-amber-800 hover:bg-amber-100"
                          onClick={() => handleAddSuggestion(suggestion)}
                        >
                          <Plus className="mr-2 h-3 w-3" />
                          {suggestion}
                        </Button>
                      ))}
                    </div>
                  </AlertDescription>
                </div>
              </div>
            </Alert>
          )}
          <VariableTable
            variables={variables}
            selectedVariables={selectedVariables}
            onSelectVariable={handleSelectVariable}
            onSelectAll={handleSelectAll}
            onEditVariable={handleOpenEditDialog}
            onDeleteVariable={handleDeleteVariable}
            onAddVariable={handleOpenAddDialog}
          />
        </CardContent>
      </Card>

      {/* Dialogs */}
      {(dialogMode === "add" || dialogMode === "edit") && (
        <VariableDialog
          isOpen={openDialog}
          onClose={handleCloseDialog}
          variable={currentVariable}
          onInputChange={handleInputChange}
          onCheckboxChange={handleCheckboxChange}
          onSave={handleSaveVariable}
          mode={dialogMode}
        />
      )}

      {dialogMode === "import" && (
        <ImportDialog
          isOpen={openDialog}
          onClose={handleCloseDialog}
          importText={importText}
          setImportText={setImportText}
          onImport={handleImportVariables}
        />
      )}

      {dialogMode === "copy" && (
        <CopyDialog
          isOpen={openDialog}
          onClose={handleCloseDialog}
          selectedVariablesCount={selectedVariables.length}
          loadingRepositories={loadingRepositories}
          repoSearchTerm={repoSearchTerm}
          setRepoSearchTerm={handleRepoSearchChange}
          targetRepositories={targetRepositories}
          selectedTargetRepos={selectedTargetRepos}
          onToggleTargetRepo={handleToggleTargetRepo}
          onCopyVariables={handleCopyVariables}
        />
      )}
    </div>
  );
};

export default VariablesPage;
